// What a Search row shows (DESIGN-SPEC §3.4, §6.18): results grouped by kind, and a
// one-line human snippet with the searched words marked. A snippet is never raw
// markdown, JSON or CSV: markdown is parsed and read as text, and data formats show
// when they changed instead.
import { markdownPlainText } from './plain-text';
import type { WorkspaceFindKind } from './types';

export type SearchGroupId = 'chats' | 'results' | 'files' | 'skills' | 'knowledge' | 'meetings';

const groupOfKind: Record<WorkspaceFindKind, SearchGroupId> = {
  chat: 'chats',
  result: 'results',
  file: 'files',
  skill: 'skills',
  skill_draft: 'skills',
  knowledge_page: 'knowledge',
  meeting: 'meetings',
};

/** Hits grouped by kind. Groups keep the order in which their best-ranked hit
 * arrived and rows keep their rank, so grouping never demotes the best match.
 * A hit of a kind this client does not show (a retired kind) is left out. */
export function groupSearchHits<T extends { kind: WorkspaceFindKind }>(hits: readonly T[]) {
  const groups = new Map<SearchGroupId, T[]>();
  for (const hit of hits) {
    if (!Object.hasOwn(groupOfKind, hit.kind)) continue;
    const id = groupOfKind[hit.kind];
    const items = groups.get(id);
    if (items) items.push(hit);
    else groups.set(id, [hit]);
  }
  return [...groups].map(([id, items]) => ({ id, items }));
}

export type SnippetPart = { text: string; match: boolean };
export type SearchSnippet =
  | { type: 'text'; parts: SnippetPart[] }
  | { type: 'updated'; at: string }
  | null;

export interface SnippetSource {
  kind: WorkspaceFindKind;
  title: string;
  excerpt?: string;
  updatedAt?: string;
  sourceKind?: unknown;
}

// Stored formats whose text is data rather than prose: a result's table, chart or
// deck payload, and CSV or JSON files. `sourceKind` is the corpus's own format field.
const dataFormats = new Set([
  'table',
  'chart',
  'deck',
  'text/csv',
  'text/tab-separated-values',
  'application/json',
]);
// A Search row shows about 85 characters of snippet on a desktop and 36 to 45 on
// a phone. A match further in than `lead` characters moves the window to start
// lead / 2 characters before it, so the first marked word begins on the visible
// line; a mark that would not end within `visible` characters is left unmarked,
// since a highlight cut by the ellipsis reads as a glitch.
const WIDE = { lead: 48, visible: 80 };
const NARROW = { lead: 20, visible: 36 };
type RowWidth = typeof WIDE;
const LENGTH = 240; // enough to overflow one line; CSS draws the ellipsis

const segmenter = (locale: string) =>
  typeof Intl.Segmenter === 'function' ? new Intl.Segmenter(locale, { granularity: 'word' }) : null;

/** The searched words, lower-cased; single letters would mark nearly every row. */
function searchTerms(query: string, locale: string) {
  const words = segmenter(locale)?.segment(query) ?? [];
  const terms = new Set<string>();
  for (const { segment, isWordLike } of words)
    if (isWordLike && segment.length > 1) terms.add(segment.toLocaleLowerCase(locale));
  return [...terms];
}

/** Parts joined where their marking now agrees. */
function append(parts: SnippetPart[], text: string, match: boolean) {
  const last = parts.at(-1);
  if (last && last.match === match) last.text += text;
  else parts.push({ text, match });
}

/** Marks only where the row shows them whole. */
function visibleMarks(parts: SnippetPart[], visible: number) {
  const kept: SnippetPart[] = [];
  let offset = 0;
  for (const part of parts) {
    append(kept, part.text, part.match && offset + part.text.length <= visible);
    offset += part.text.length;
  }
  return kept;
}

/** Words that start with a searched word are marked, so "price" marks "prices". */
function markWords(
  text: string,
  terms: string[],
  locale: string,
  { lead, visible }: RowWidth,
): SnippetPart[] {
  const words = segmenter(locale);
  if (!words || !terms.length) return [{ text: text.slice(0, LENGTH), match: false }];
  const segments = [...words.segment(text)].map(({ segment, index, isWordLike }) => ({
    segment,
    index,
    match:
      Boolean(isWordLike) &&
      terms.some((term) => segment.toLocaleLowerCase(locale).startsWith(term)),
  }));
  const first = segments.find((segment) => segment.match);
  // Start a few words before the first match, so the match is on the visible line.
  const start =
    first && first.index > lead
      ? (segments.find(
          (segment) => segment.index >= first.index - lead / 2 && segment.segment.trim(),
        )?.index ?? 0)
      : 0;
  const parts: SnippetPart[] = start ? [{ text: '… ', match: false }] : [];
  for (const segment of segments) {
    if (segment.index < start) continue;
    if (segment.index >= start + LENGTH) break;
    append(parts, segment.segment, segment.match);
  }
  return visibleMarks(parts, visible);
}

// Punctuation that joined a leading title to the text after it ("Title. Body").
const titleJoiners = new Set(['.', ',', ':', ';', '·', '-', '–', '—', '|']);
function afterTitle(text: string) {
  let rest = text.trim();
  while (rest && titleJoiners.has(rest[0])) rest = rest.slice(1).trim();
  return rest;
}

/** A one-line snippet for a Search row, or when it changed for data formats and
 * for rows whose stored text only repeats their title. `narrow`: a phone-width
 * row, whose shorter line needs a shorter lead before the first match. */
export function searchSnippet(
  hit: SnippetSource,
  query: string,
  locale = 'en',
  { narrow = false }: { narrow?: boolean } = {},
): SearchSnippet {
  const updated = hit.updatedAt ? { type: 'updated' as const, at: hit.updatedAt } : null;
  if (typeof hit.sourceKind === 'string' && dataFormats.has(hit.sourceKind)) return updated;
  let text = markdownPlainText(hit.excerpt ?? '', { pdf: hit.sourceKind === 'application/pdf' });
  // A chat's stored text begins with its title; the row already shows it.
  if (hit.title && text.startsWith(hit.title)) {
    const rest = text.slice(hit.title.length);
    // A title must end at a boundary: "Cost" is not the title of "Costs rose".
    if (!rest || /^\s/.test(rest) || titleJoiners.has(rest[0])) text = afterTitle(rest);
  }
  if (!text) return updated;
  return {
    type: 'text',
    parts: markWords(text, searchTerms(query, locale), locale, narrow ? NARROW : WIDE),
  };
}
